"use client";

import { useEffect, useState } from "react";
import {
  useMidiController,
  useMidiInputs,
  type DevicePermission,
} from "@/contexts/studio-context";
import { Note } from "@/lib/music/note";
import type { MidiDeviceInfo } from "@/lib/piano/input/midi";
import { Group, InlineLink, Line, PermBadge, SectionHeader, Switch } from "./atoms";
import { FONT_MONO, SET } from "./palette";

/**
 * Maps the heuristic transport tag from `MidiDeviceInfo` to a short display
 * label used inline beneath the device name.
 */
const TRANSPORT_LABEL: Record<MidiDeviceInfo["transport"], string> = {
  usb: "usb",
  bluetooth: "bluetooth",
  virtual: "virtuel",
};

interface MidiSectionProps {
  /** Number of *active* (enabled + connected) inputs / total, drives the kicker counter. */
  countLabel: string;
}

export function MidiSection({ countLabel }: MidiSectionProps) {
  const {
    devices,
    permission,
    activeDeviceId,
    setActiveDevice,
    setEnabled,
    requestAccess,
    rescan,
  } = useMidiInputs();

  const isGranted = permission === "granted";
  const isUnsupported = permission === "unsupported";
  const showBanner = !isGranted && !isUnsupported;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        kicker={`Entrées MIDI · ${countLabel}`}
        trailing={
          <>
            <PermBadge
              permission={permission}
              onRequest={permission === "granted" ? undefined : requestAccess}
            />
            {isGranted ? <InlineLink onClick={rescan}>↻ rescan</InlineLink> : null}
          </>
        }
      />

      {showBanner ? <PermissionBanner permission={permission} onRequest={requestAccess} /> : null}

      {isUnsupported ? <UnsupportedNotice /> : null}

      {isGranted ? (
        <Group>
          {devices.length === 0 ? (
            <EmptyDeviceRow />
          ) : (
            devices.map((d, idx) => (
              <DeviceRow
                key={d.id}
                device={d}
                isActive={activeDeviceId === d.id}
                isFirst={idx === 0}
                onSetActive={() => setActiveDevice(d.id)}
                onToggleEnabled={(next) => setEnabled(d.id, next)}
              />
            ))
          )}
          <LiveTestFooter
            anyEnabled={devices.some((d) => d.enabled && d.connected)}
            withDivider
          />
        </Group>
      ) : null}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Navigateur sans Web MIDI : on nomme les entrées qui restent.
// ──────────────────────────────────────────────────────────────

/**
 * Ce qui s'affichait avant à la place : rien.
 *
 * `showBanner` excluait explicitement `unsupported`, et la liste comme le
 * testeur sont conditionnés à `granted`. Sur un navigateur sans Web MIDI, la
 * section se réduisait donc à son titre et à une pastille « INDISPONIBLE » non
 * cliquable. Or c'est précisément le cas de Safari sur iPhone et iPad, c'est-à
 * -dire de la tablette posée sur le pupitre, la posture de référence du
 * produit : on y présentait une porte d'entrée vide comme si c'était la seule.
 *
 * Le MIDI n'est pas la seule entrée de Virtuosa, il est juste la première de la
 * liste. Quand il manque, il faut nommer les deux autres plutôt que laisser
 * l'utilisateur conclure qu'il ne peut pas jouer.
 */
function UnsupportedNotice() {
  return (
    <div
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: `1px solid ${SET.border}`,
        background: "rgba(139, 92, 246, 0.05)",
      }}
    >
      <span style={{ fontSize: 12, color: SET.textDim, lineHeight: 1.4 }}>
        Ce navigateur n&apos;expose pas le MIDI, c&apos;est le cas de Safari sur
        iPhone et iPad. Ton clavier reste jouable : active le micro juste en
        dessous pour qu&apos;il t&apos;écoute, ou sers-toi du clavier à
        l&apos;écran.
      </span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Permission banner, visible until the user grants access. Mirrors
// the mockup's tinted block above the device list.
// ──────────────────────────────────────────────────────────────

function PermissionBanner({
  permission,
  onRequest,
}: {
  permission: DevicePermission;
  onRequest: () => void;
}) {
  const denied = permission === "denied";
  const message = denied
    ? "L'accès MIDI a été refusé. Autorise-le dans les paramètres du navigateur pour brancher un clavier."
    : "Branche un clavier MIDI puis autorise l'accès, la liste se mettra à jour automatiquement.";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 14px",
        borderRadius: 10,
        border: `1px solid ${SET.border}`,
        background: "rgba(139, 92, 246, 0.05)",
      }}
    >
      <span style={{ fontSize: 12, color: SET.textDim, lineHeight: 1.4 }}>{message}</span>
      {!denied ? (
        <button
          type="button"
          onClick={onRequest}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            background: SET.violet,
            color: "#fff",
            border: `1px solid ${SET.violet}`,
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            boxShadow: `0 0 12px ${SET.violetGlow}`,
          }}
        >
          Autoriser
        </button>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Device row, radio dot (active), name + connection dot, type/meta,
// per-device on/off switch.
// ──────────────────────────────────────────────────────────────

interface DeviceRowProps {
  device: MidiDeviceInfo;
  isActive: boolean;
  isFirst: boolean;
  onSetActive: () => void;
  onToggleEnabled: (next: boolean) => void;
}

function DeviceRow({ device, isActive, isFirst, onSetActive, onToggleEnabled }: DeviceRowProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderTop: isFirst ? "none" : `1px solid ${SET.border}`,
        minHeight: 48,
      }}
    >
      <button
        type="button"
        onClick={onSetActive}
        title={isActive ? "Périphérique actif" : "Définir comme actif"}
        aria-pressed={isActive}
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: `1px solid ${isActive ? SET.violet : SET.borderHi}`,
          background: "transparent",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "border-color 150ms",
        }}
      >
        {isActive ? (
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: SET.violet,
              boxShadow: `0 0 6px ${SET.violetGlow}`,
            }}
          />
        ) : null}
      </button>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13,
              color: SET.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {device.name}
          </span>
          {device.connected ? (
            <span
              aria-hidden
              title="Connecté"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: SET.green,
                boxShadow: `0 0 6px ${SET.greenGlow}`,
                flexShrink: 0,
              }}
            />
          ) : null}
        </div>
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            letterSpacing: "0.08em",
            color: SET.textMute,
            textTransform: "lowercase",
          }}
        >
          {TRANSPORT_LABEL[device.transport]}
          {device.manufacturer ? ` · ${device.manufacturer.toLowerCase()}` : ""}
        </span>
      </div>

      <Switch
        checked={device.enabled}
        onChange={onToggleEnabled}
        label={`Activer ${device.name}`}
      />
    </div>
  );
}

function EmptyDeviceRow() {
  return (
    <div
      style={{
        padding: "16px 14px",
        textAlign: "center",
        color: SET.textMute,
        fontSize: 12,
      }}
    >
      Aucun périphérique MIDI détecté.
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Live test footer, subscribes to the WebMIDI activity stream so
// the user gets a "did the cable work?" feedback right in the panel.
// ──────────────────────────────────────────────────────────────

interface LiveTestFooterProps {
  anyEnabled: boolean;
  withDivider?: boolean;
}

function LiveTestFooter({ anyEnabled, withDivider }: LiveTestFooterProps) {
  const midi = useMidiController();
  const [last, setLast] = useState<{ midi: number; velocity: number } | null>(null);

  useEffect(() => {
    if (!midi) {
      return;
    }

    const input = midi.getInput();

    if (!input) {
      return;
    }

    return input.onActivity(({ midi: midiNote, velocity }) => {
      setLast({ midi: midiNote, velocity });
    });
  }, [midi]);

  // Faded copy when no input is enabled, avoids a stale "C4 · vel 84" lingering.
  const noteLabel = last
    ? Note.fromMidi(last.midi).toName({ withOctave: true })
    : ", ";
  const velLabel = last ? last.velocity : ", ";

  return (
    <Line
      withDivider={withDivider ?? true}
      label={
        <span style={{ fontSize: 12, color: SET.textDim }}>
          {anyEnabled
            ? "Joue une note pour vérifier"
            : "Active un périphérique pour tester"}
        </span>
      }
      control={
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11,
            letterSpacing: "0.06em",
            color: last ? SET.violetLight : SET.textVeryMute,
          }}
        >
          {noteLabel} · vel {velLabel}
        </span>
      }
    />
  );
}
